import { getTranslatedFacetName } from "@utils/search/facetTranslation.utils";
import { getInvalidationsForComponent } from "@utils/search/invalidation.utils";

import {
    toHierarchicalFacet,
    toHierarchicalFacetPath,
} from "@services/bffSearch/responseTransformers/mergeHierarchicalFacets.transformer";

import {
    clearFacetValue,
    clearHierarchicalFacet,
    setNumericFacet,
} from "@redux/modules/value/search/search.actions";

import { FacetType } from "@components/Search/SearchResults/StateComponents/Facets/Facet/@types/facetType.types";
import {
    mapToFacetValueInMillimetres,
    mapToFacetValueWithDisplayValue,
} from "@components/Search/SearchResults/StateComponents/Facets/Facet/components/UnitRangeFacet/unitRange.helpers";
import { SelectedFilterMapper } from "@components/Search/SearchResults/StateComponents/SelectedFacets/selectedFacets.types";

import { FACET_DELIMITER } from "../Facets/Facet/facet.configuration";

const defaultFacetMapper: SelectedFilterMapper<FacetType.DEFAULT> = (
    facet,
    filters,
    dispatch,
    component,
    t,
) =>
    filters.facetFilter?.selectedValues?.map(selectedFacetValue => ({
        label: `${getTranslatedFacetName(
            facet.name,
            t,
        )}: ${selectedFacetValue}`,
        onClear: () =>
            dispatch(
                clearFacetValue(
                    component,
                    facet.name,
                    selectedFacetValue,
                    getInvalidationsForComponent(component),
                ),
            ),
    }));

const colorswatchFacetMapper: SelectedFilterMapper<FacetType.COLORSWATCH> = (
    facet,
    filters,
    dispatch,
    component,
    t,
) =>
    facet.values
        .filter(facetValue =>
            filters.facetFilter?.selectedValues.includes(facetValue.value),
        )
        .map(facetValue => ({
            label: `${getTranslatedFacetName(facet.name, t)}: ${
                facetValue.name
            }`,
            onClear: () =>
                dispatch(
                    clearFacetValue(
                        component,
                        facet.name,
                        facetValue.value,
                        getInvalidationsForComponent(component),
                    ),
                ),
        }));

const unitRangeFacetMapper: SelectedFilterMapper<FacetType.UNIT_RANGE> = (
    facet,
    filters,
    dispatch,
    component,
    t,
) =>
    facet.values
        .filter(facetValue =>
            filters.facetFilter?.selectedValues.includes(facetValue.value),
        )
        .map(mapToFacetValueInMillimetres)
        .map(mapToFacetValueWithDisplayValue)
        .map(({ displayValue, value }) => {
            const facetName = getTranslatedFacetName(facet.name, t);

            return {
                label: `${facetName}: ${displayValue}`,
                onClear: () =>
                    dispatch(
                        clearFacetValue(
                            component,
                            facet.name,
                            value,
                            getInvalidationsForComponent(component),
                        ),
                    ),
            };
        });

const numericFacetMapper: SelectedFilterMapper<FacetType.NUMERIC> = (
    facet,
    filters,
    dispatch,
    component,
    t,
) => {
    if (!filters.numericFilter) return;

    const numericFilter = filters.numericFilter;
    const lowerBound =
        numericFilter.lowerBound?.toString().replace(".", ",") ?? "*";
    const upperBound =
        numericFilter.upperBound?.toString().replace(".", ",") ?? "*";

    return [
        {
            label: `${getTranslatedFacetName(
                facet.name,
                t,
            )}: ${lowerBound} - ${upperBound}`,
            onClear: () =>
                dispatch(
                    setNumericFacet(
                        component,
                        numericFilter.name,
                        undefined,
                        getInvalidationsForComponent(component),
                    ),
                ),
        },
    ];
};

const hierarchicalFacetMapper: SelectedFilterMapper<FacetType.HIERARCHICAL> = (
    facet,
    filters,
    dispatch,
    component,
    _t,
) => {
    const label = filters.facetFilter?.selectedValues.at(0);
    if (!label) return [];

    const parsedLabel = toHierarchicalFacet(
        toHierarchicalFacetPath(label).map(
            lvl => lvl.split(FACET_DELIMITER).at(2)!,
        ),
    );

    return [
        {
            label: parsedLabel,
            onClear: () => {
                dispatch(
                    clearHierarchicalFacet(
                        component,
                        facet.name,
                        getInvalidationsForComponent(component),
                    ),
                );
            },
        },
    ];
};

const facetTypeToLabelMapperMap = {
    [FacetType.DEFAULT]: defaultFacetMapper,
    [FacetType.COLORSWATCH]: colorswatchFacetMapper,
    [FacetType.UNIT_RANGE]: unitRangeFacetMapper,
    [FacetType.NUMERIC]: numericFacetMapper,
    [FacetType.HIERARCHICAL]: hierarchicalFacetMapper,
} as const;
export const getLabelMapperByFacetType = <TFacetType extends FacetType>(
    facetType: TFacetType,
) => facetTypeToLabelMapperMap[facetType] as SelectedFilterMapper<TFacetType>;
